import React from "react";
import { Button } from "primereact/button";
import { Slider, SliderChangeEvent } from "primereact/slider";
import { MdCalendarMonth } from "react-icons/md";
import { FaArrowTrendUp, FaUserClock } from "react-icons/fa6";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";

interface AssetValueChartFilterProps {
  selectedChartFilter: string;
  onChartFilterChange: (filter: string) => void;
  showProjections: boolean;
  onProjectionsToggle: () => void;
  enableAgeFilter?: boolean;
  enablePlanYearFilter?: boolean;
  enableZoom?: boolean;
  zoomValue?: [number, number];
  zoomMax?: number;
  onZoomChange?: (value: [number, number]) => void;
}

const AssetValueChartFilter: React.FC<AssetValueChartFilterProps> = ({
  selectedChartFilter,
  onChartFilterChange,
  showProjections,
  onProjectionsToggle,
  enableAgeFilter = true,
  enablePlanYearFilter = true,
  enableZoom = false,
  zoomValue,
  zoomMax,
  onZoomChange,
}) => {
  const globalTheme = getSessionStore("globalTheme");
  const isDark = globalTheme === THEME.DARK;

  const chartFilterOptions = [
    {
      icon: <MdCalendarMonth color={isDark ? "white" : "black"} />,
      value: "year",
      tooltip: "Year",
    },
    {
      icon: <FaArrowTrendUp color={isDark ? "white" : "black"} />,
      value: "planYear",
      tooltip: "Plan Year",
    },
    ...(enableAgeFilter
      ? [
          {
            icon: <FaUserClock color={isDark ? "white" : "black"} />,
            value: "insuredAge",
            tooltip: "Insured Age",
          },
        ]
      : []),
  ];

  const handleChartFilterClick = () => {
    const currentIndex = chartFilterOptions.findIndex((opt) => opt.value === selectedChartFilter);
    const nextIndex = (currentIndex + 1) % chartFilterOptions.length;
    onChartFilterChange(chartFilterOptions[nextIndex].value);
  };

  const currentFilterOption =
    chartFilterOptions.find((opt) => opt.value === selectedChartFilter) || chartFilterOptions[0];

  const projectionsButtonColor = (() => {
    if (showProjections) {
      return isDark ? "#4A90E2" : "#004781";
    }
    return isDark ? "#6B7280" : "#9CA3AF";
  })();

  const shouldShowZoom = enableZoom && typeof zoomMax === "number" && zoomMax > 0 && !!zoomValue;

  const handleZoomChange = (e: SliderChangeEvent) => {
    if (!onZoomChange) return;
    const { value } = e;
    if (!Array.isArray(value) || value.length !== 2) return;
    const [start, end] = value;
    if (typeof start !== "number" || typeof end !== "number") return;
    onZoomChange([start, end]);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row space-x-2 align-items-center justify-content-between">
        {/* Left side - Chart filter button */}
        <div className="flex flex-row align-items-center gap-2">
          <Button
            icon={currentFilterOption.icon}
            outlined
            size="small"
            className="p-2"
            severity="info"
            tooltipOptions={{ position: "top" }}
            tooltip={currentFilterOption.tooltip}
            onClick={enablePlanYearFilter ? handleChartFilterClick : undefined}
          />
        </div>

        {/* Right side - Projections toggle */}
        <div className="flex flex-row align-items-center gap-2">
          <Button
            icon={
              <ProjectionsIcon
                key={`projections-icon-${showProjections}`}
                fill={
                  showProjections
                    ? isDark
                      ? "#4A90E2"
                      : "#004781"
                    : isDark
                    ? "#6B7280"
                    : "#9CA3AF"
                }
              />
            }
            text
            size="small"
            className="p-2 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:shadow-none"
            severity="secondary"
            tooltipOptions={{ position: "top" }}
            tooltip={showProjections ? "Hide Projections" : "Show Projections"}
            onClick={onProjectionsToggle}
            style={{
              color: projectionsButtonColor,
              outline: "none",
              boxShadow: "none",
            }}
            pt={{
              root: {
                className: "!shadow-none focus:!shadow-none focus-visible:!shadow-none",
              },
            }}
          />
        </div>
      </div>

      {shouldShowZoom ? (
        <div className="px-1">
          <Slider
            value={zoomValue}
            min={0}
            max={zoomMax}
            range
            onChange={handleZoomChange}
            pt={{
              range: { className: isDark ? "!bg-[#A7B7CC4D]" : "!bg-[#A7B7CC66]" },
              handle: {
                className: isDark
                  ? "!border-[#4A4A60] !bg-white"
                  : "!border-[#E5E7EB] !bg-[#333333]",
              },
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(AssetValueChartFilter);
